import { HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel } from "@microsoft/signalr";
import axios from "axios";
import { Observable } from "rxjs";
import { share } from "rxjs/operators";
import { App } from "vue";

export default {
  install(app: App) {
    let connectedResolver: () => void;
    let $signalRStartedPromise: Promise<void> | undefined = new Promise((resolve) => (connectedResolver = resolve));

    function resetConnectionPromise() {
      $signalRStartedPromise = new Promise<void>((resolve) => {
        connectedResolver = resolve;
      });
    }
    let connection: HubConnection;

    const $startSignalR = (token: string) => {
      connection = new HubConnectionBuilder()
        .withUrl(`${axios.defaults.baseURL}/api/webclient/clientcommunicationhub`, { accessTokenFactory: () => token, withCredentials: false })
        .withStatefulReconnect()
        .configureLogging(LogLevel.Information)
        .build();

      async function start() {
        try {
          if (connection.state !== HubConnectionState.Connected) {
            await connection.start();
          }
        } catch (err) {
          console.error("Failed to connect with hub", err);
          setTimeout(start, 15000);
        }
      }
      connection.onclose((error) => {
        console.error(`Connection closed due to error "${error}". Try refreshing this page to restart the connection.`);
        resetConnectionPromise();
      });
      connection.onreconnected(() => {
        connectedResolver();
      });
      $subscribeToServerEvent("ServerReady", () => {
        connectedResolver();
      });
      start();
    };

    const $getConnectionId = () => {
      if (connection && connection.state == HubConnectionState.Connected) {
        return connection.connectionId;
      }
      return null;
    };

    const $subscribeToServerEvent = (event: string, handler: any) => connection.on(event, handler);

    const $unsubscribeFromServerEvent = (event: string, handler: any) => connection.off(event, handler);

    const $subscribeFromStream = (event: string, args: any, handler: any, errorHandler: any, completionHandler: any) => {
      return fromStream(connection, event, args).subscribe({
        next: (value) => {
          handler(value);
        },
        error: (error) => {
          errorHandler(error);
        },
        complete: () => {
          completionHandler();
        }
      });
    };

    const $stopSignalR = () => {
      if (connection) connection.stop();
    };

    app.provide("$startSignalR", $startSignalR);
    app.provide("$signalRStartedPromise", $signalRStartedPromise);
    app.provide("$getConnectionId", $getConnectionId);
    app.provide("$subscribeToServerEvent", $subscribeToServerEvent);
    app.provide("$unsubscribeFromServerEvent", $unsubscribeFromServerEvent);
    app.provide("$subscribeFromStream", $subscribeFromStream);
    app.provide("$stopSignalR", $stopSignalR);
  }
};

const fromStream = (connection: HubConnection, streamName: string, args: any[]) => {
  return new Observable((observer) => {
    const stream = connection.stream(streamName, args);
    const subscription = stream.subscribe(observer);

    return () => {
      subscription.dispose();
    };
  }).pipe(share());
};
